<template>
  <div class="yt-grid">
    <yt-chart-card
      class="yt-span-5"
      title="Revenus par site"
      subtitle="Revenus estimés en euros, et RPM."
      :sources="['yt']"
      :columns="siteColumns"
      :rows="bySite"
      :loading="loading"
      :empty="bySite.length === 0"
    >
      <vue-apex-charts type="bar" :height="Math.max(160, bySite.length * 34 + 40)" :options="siteOptions" :series="[{ name: 'Revenus', data: bySite.map((row) => Number(row.revenue.toFixed(2))) }]" />
    </yt-chart-card>
    <yt-chart-card
      class="yt-span-7"
      title="RPM et CPM"
      :subtitle="`Deux mesures en euros, par ${granularityLabel}. RPM : revenu pour 1 000 vues. CPM : revenu brut pour 1 000 impressions publicitaires.`"
      :sources="['yt']"
      :columns="rpmColumns"
      :rows="buckets"
      :loading="!overview"
      :empty="buckets.length === 0"
    >
      <vue-apex-charts type="line" height="320" :options="rpmOptions" :series="rpmSeries" />
    </yt-chart-card>
  </div>
</template>

<script>
import { computed, ref, toRefs } from '@vue/composition-api'
import VueApexCharts from 'vue-apexcharts'
import YtChartCard from '../YtChartCard.vue'
import useYoutubePerf from '../useYoutubePerf'
import { COLORS, GRANULARITY_LABELS, baseChartOptions, bucketLabel, bucketTitle, fEur, fEur2, fEurAxis, fInt } from '../ytFormat'

export default {
  name: 'YtMonetizationTab',
  components: { VueApexCharts, YtChartCard },
  props: { active: { type: Boolean, default: false } },
  setup(props) {
    const { active } = toRefs(props)
    const { store, get, ensureOverview, useTabLoader } = useYoutubePerf()
    const bySite = ref([])
    const loading = ref(false)
    const fetchMonetization = async () => {
      loading.value = true
      try {
        const { data } = await get('/monetization')
        bySite.value = (data.by_site || []).slice().sort((a, b) => b.revenue - a.revenue)
      } finally {
        loading.value = false
      }
    }
    useTabLoader(active, () => Promise.all([ensureOverview(), fetchMonetization()]))

    const overview = computed(() => store.getters['youtubePerf/getOverview'])
    const buckets = computed(() => overview.value?.series?.buckets || [])
    const granularity = computed(() => overview.value?.series?.granularity || 'day')
    const granularityLabel = computed(() => GRANULARITY_LABELS[granularity.value])
    const titles = computed(() => buckets.value.map((b) => bucketTitle(b, granularity.value)))

    const siteOptions = computed(() => ({
      ...baseChartOptions(),
      colors: [COLORS.revenue],
      plotOptions: { bar: { horizontal: true, borderRadius: 5, barHeight: '60%' } },
      xaxis: { categories: bySite.value.map((row) => row.label), labels: { style: { colors: '#94A3B8', fontSize: '10px', fontWeight: 600 }, formatter: (v) => fEurAxis(v) }, axisBorder: { show: false }, axisTicks: { show: false } },
      yaxis: { labels: { style: { colors: '#475569', fontSize: '11px', fontWeight: 600 } } },
      tooltip: { y: { formatter: (v, { dataPointIndex }) => `${fEur(v)} · RPM ${fEur2(bySite.value[dataPointIndex]?.rpm)}` } },
    }))
    const siteColumns = [
      { key: 'label', label: 'Site', numeric: false },
      { key: 'revenue', label: 'Revenus', format: fEur },
      { key: 'views', label: 'Vues', format: fInt },
      { key: 'rpm', label: 'RPM', format: fEur2 },
      { key: 'cpm', label: 'CPM', format: fEur2 },
    ]

    const rpmSeries = computed(() => [
      { name: 'RPM', data: buckets.value.map((b) => Number(b.rpm.toFixed(2))) },
      { name: 'CPM', data: buckets.value.map((b) => Number(b.cpm.toFixed(2))) },
    ])
    const rpmOptions = computed(() => ({
      ...baseChartOptions(),
      colors: [COLORS.rpm, COLORS.cpm],
      stroke: { width: [3, 2], curve: 'smooth' },
      markers: { size: buckets.value.length > 60 ? 0 : 3, strokeWidth: 2, strokeColors: '#fff', hover: { size: 5 } },
      legend: { show: true, position: 'top', horizontalAlign: 'left', fontWeight: 600 },
      xaxis: { ...baseChartOptions().xaxis, categories: buckets.value.map((b) => bucketLabel(b, granularity.value)) },
      yaxis: { labels: { style: { colors: '#94A3B8', fontSize: '10px', fontWeight: 600 }, formatter: (v) => fEur2(v) }, min: 0 },
      tooltip: { shared: true, intersect: false, x: { formatter: (_, { dataPointIndex }) => titles.value[dataPointIndex] || '' }, y: { formatter: (v) => fEur2(v) } },
    }))
    const rpmColumns = [
      { key: 'label', label: 'Période', numeric: false, format: (_, row) => bucketTitle(row, granularity.value) },
      { key: 'rpm', label: 'RPM', format: fEur2 },
      { key: 'cpm', label: 'CPM', format: fEur2 },
      { key: 'revenue', label: 'Revenus', format: fEur },
      { key: 'ad_impressions', label: 'Impressions publicitaires', format: fInt },
    ]

    return { overview, buckets, granularityLabel, bySite, loading, siteOptions, siteColumns, rpmSeries, rpmOptions, rpmColumns }
  },
}
</script>

<style lang="scss" scoped>
.yt-grid { display: grid; grid-template-columns: repeat(12, minmax(0, 1fr)); gap: $rmr-space-lg; }
.yt-span-5 { grid-column: span 5; }
.yt-span-7 { grid-column: span 7; }
@media (max-width: 1100px) { .yt-span-5, .yt-span-7 { grid-column: span 12; } }
</style>
